import React from 'react';
import { devWarning } from './devWarning';
import { getComponentName } from './getComponentName';
import { isElementOfType } from './isElementOfType';
import { isUnrenderable } from './isUnrenderable';
import { CoreUITypedComponent } from './types/CoreUITypedComponent';
import { OmitStrict } from './types/OmitStrict';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FilterChildrenFunction = (child: React.ReactElement<any>) => boolean;

type ExpectedType = string;

type OneOrMany<T> = T | T[];

const getName = (type: ExpectedType | React.ReactType) => `${getComponentName(type)} elements`;

const getNames = (type: OneOrMany<ExpectedType>) =>
  Array.isArray(type) ? type.map((t) => getName(t)).join(', ') : getName(type);

export function filterChildren<T>(
  children: React.ReactNode,
  filterFn: FilterChildrenFunction,
  expected: OneOrMany<ExpectedType>,
  parent: React.ReactType | string,
) {
  const expectedName = getNames(expected);
  const parentName = getName(parent);

  return (
    React.Children.map(children, (c) => {
      if (!React.isValidElement<T>(c) || !filterFn(c)) {
        devWarning(
          isUnrenderable(c),
          `Found unexpected ${
            React.isValidElement(c) ? `"${c.type}" element` : 'extra content'
          } inside ${parentName}. This content will be ignored. Only ${expectedName} are allowed.`,
        );

        return null;
      }

      return c;
    }) || []
  ).filter(Boolean);
}

/**
 * Filter a React component's children to only those of a particular expected
 * type, issuing dev warnings when any other content or elements are found.
 *
 * @param children The children to filter
 * @param expected The expected element type for each child
 * @param parent The component performing the filtering; used for warning
 * messages when unexpected children are received
 */
export function filterChildrenByType<T>(
  children: React.ReactNode,
  expected: OneOrMany<ExpectedType>,
  parent: React.ReactType | string,
) {
  return filterChildren<T>(
    children,
    (c) =>
      ([] as ExpectedType[])
        .concat(expected)
        .some((t) => isElementOfType(c.type as CoreUITypedComponent, t)),
    expected,
    parent,
  );
}

export function withFilteredChildren(
  expected: OneOrMany<ExpectedType>,
  filterFn?: FilterChildrenFunction,
  parent?: React.ReactType | string,
) {
  return <P extends { children: C }, C extends C0[] = P['children'], C0 = C[0]>(
    Component: React.ComponentType<P>,
  ) => {
    // The returned component should accept single children or arrays of
    // children, even though the wrapped component will always receive an array.
    type AcceptedProps = OmitStrict<P, 'children'> & {
      children: C0 | C;
    };

    type WrapperProps = AcceptedProps & { forwardRef?: React.Ref<P> };

    function WrapperComponent({ children, forwardRef, ...others }: WrapperProps) {
      const filteredChildren = filterFn
        ? filterChildren(children, filterFn, expected, parent || Component)
        : filterChildrenByType(children, expected, parent || Component);

      const props = {
        ref: forwardRef,
        children: filteredChildren,
        ...others,
      } as unknown as P;

      return <Component {...props} />;
    }

    WrapperComponent.displayName = `WithFilteredChildren(${getComponentName(Component)})`;

    return React.forwardRef<WrapperProps, AcceptedProps>((props, ref) => (
      <WrapperComponent forwardRef={ref} {...(props as WrapperProps)} />
    ));
  };
}
